import { stringify } from "yaml";

export type DiffSegment = {
	/** Note: this diff only ever emits "removed" | "added" | "unchanged"; a modified line surfaces
	 *  as a removed+added pair. "changed" stays in the union for renderers that merge such pairs. */
	type: "removed" | "added" | "unchanged" | "changed";
	left?: string;
	right?: string;
};

/**
 * Line-based diff of two objects, compared through their YAML serialization. YAML (not JSON) so the
 * deployment diff modal shows one field per line without brace/comma churn, and key order comes out
 * stable for equal inputs. Classic O(n*m) LCS alignment : fine for page-sized payloads, not meant
 * for megabyte documents. Non-object inputs diff as empty (everything shows added/removed).
 */
export function objectDiffToSegments(leftObj: unknown, rightObj: unknown): DiffSegment[] {
	const leftStr = typeof leftObj === "object" && leftObj !== null ? stringify(leftObj, { indent: 2 }).trim() : "";
	const rightStr = typeof rightObj === "object" && rightObj !== null ? stringify(rightObj, { indent: 2 }).trim() : "";
	const leftLines = leftStr ? leftStr.split(/\r?\n/) : [];
	const rightLines = rightStr ? rightStr.split(/\r?\n/) : [];

	const n = leftLines.length;
	const m = rightLines.length;

	// LCS table for alignment
	const dp: number[][] = Array(n + 1).fill(null).map(() => Array(m + 1).fill(0));
	for (let i = 1; i <= n; i++) {
		for (let j = 1; j <= m; j++) {
			if (leftLines[i - 1] === rightLines[j - 1]) {
				dp[i][j] = dp[i - 1][j - 1] + 1;
			} else {
				dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
			}
		}
	}

	// Backtrack to get alignment
	let i = n;
	let j = m;
	const aligned: DiffSegment[] = [];
	while (i > 0 || j > 0) {
		if (i > 0 && j > 0 && leftLines[i - 1] === rightLines[j - 1]) {
			aligned.push({ type: "unchanged", left: leftLines[i - 1], right: rightLines[j - 1] });
			i--;
			j--;
		} else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
			aligned.push({ type: "added", right: rightLines[j - 1] });
			j--;
		} else if (i > 0) {
			aligned.push({ type: "removed", left: leftLines[i - 1] });
			i--;
		}
	}
	return aligned.reverse();
}

/** Contexte gardé de part et d'autre d'un pli de lignes inchangées. */
const FOLD_CONTEXT = 2;

export type RenderRow =
	| { kind: "line"; segment: DiffSegment }
	| { kind: "fold"; hidden: number };

/**
 * Replie les plages inchangées trop longues d'un diff unifié, en gardant FOLD_CONTEXT lignes
 * autour des changements (et rien aux extrémités du document) : le lecteur voit les hunks,
 * pas le document entier.
 */
export function foldUnchanged(segments: DiffSegment[]): RenderRow[] {
	const rows: RenderRow[] = [];
	let i = 0;
	while (i < segments.length) {
		if (segments[i].type !== "unchanged") {
			rows.push({ kind: "line", segment: segments[i] });
			i++;
			continue;
		}
		let j = i;
		while (j < segments.length && segments[j].type === "unchanged") j++;
		const run = j - i;
		const keepStart = i === 0 ? 0 : FOLD_CONTEXT;
		const keepEnd = j === segments.length ? 0 : FOLD_CONTEXT;
		if (run > keepStart + keepEnd + 1) {
			for (let k = i; k < i + keepStart; k++) rows.push({ kind: "line", segment: segments[k] });
			rows.push({ kind: "fold", hidden: run - keepStart - keepEnd });
			for (let k = j - keepEnd; k < j; k++) rows.push({ kind: "line", segment: segments[k] });
		} else {
			for (let k = i; k < j; k++) rows.push({ kind: "line", segment: segments[k] });
		}
		i = j;
	}
	return rows;
}
